import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Caption1,
  MessageBar,
  MessageBarBody,
  Spinner,
  Tab,
  TabList,
  Text,
  makeStyles,
} from '@fluentui/react-components';
import type { StepAnswers, StepSection } from '@vigeo/shared';
import { KeyValueList, tokens, type KeyValueItem } from '@vigeo/ui';
import { PortalStepRenderer } from '@/steps';
import { fetchStepAnswers } from '@/lib/api.js';

/**
 * HR opens one onboarding form: "Answers so far" and "What the candidate sees" (ADR-013 §4, #211).
 *
 * The second tab renders the step type's OWN portal component, read-only — not a re-drawing.
 * That is the entire point: a re-drawn copy drifts from what the candidate actually sees, and the
 * difference would only ever be discovered by a candidate insisting they answered something the
 * screen does not show. `PortalStepRenderer` folds `readOnly` into `disabled` once for every
 * registered type, so a step type nobody has written yet is read-only-correct here by default.
 *
 * `config` arrives as a PROP rather than from the answers endpoint, and deliberately: the pinned
 * config belongs to the instance, every caller that can open a form already holds it, and the
 * endpoint's job is the answers. Passing it in also keeps this component renderable in a test
 * without a network.
 */

const useStyles = makeStyles({
  panel: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  sections: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  section: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'baseline' },
  value: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
  attempts: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  signing: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
});

export interface StepAnswersPanelProps {
  readonly personId: string;
  readonly instanceId: string;
  readonly type: string;
  /** The instance's PINNED config. See the note above on why this is not fetched here. */
  readonly config: unknown;
  /** The banner names the candidate, so the copy needs their first name (ADR-013 §4). */
  readonly candidateFirstName: string;
}

const SECTION_LABELS: Record<StepSection['state'], string> = {
  done: 'Done',
  'in-progress': 'In progress',
  'not-started': 'Not started',
};

/**
 * The fixed banner, copied from ADR-013 §4 rather than paraphrased.
 *
 * `tests/step-answers-panel.test.tsx` asserts the sent-yet copy word for word, because this is the
 * sentence that tells HR they are looking at something they must not act on — and a reassuring
 * paraphrase of it is worse than none.
 */
function bannerFor(answers: StepAnswers, firstName: string): { intent: 'info' | 'warning'; text: string } {
  if (answers.state === 'approved') {
    /**
     * ADR-013 §4 asks for "Reviewed by … on …", and this says "Reviewed on …". The date is real;
     * the name is not available to say.
     *
     * `reviewedBy` is `CurrentUser.userId` — Azure Static Web Apps' opaque principal id, which is
     * what the approve path has always written. NOTHING in this system records a reviewer's display
     * name: not `StepInstance`, not `AuditEvent` (which stores `actorId` and `actorRoles` only).
     * Printing that id would put a meaningless string where HR expects a colleague's name, and
     * printing a name we never recorded would be worse. So the sentence covers what is recorded,
     * and #211 carries the proposal to capture the display name at approval time.
     */
    const on = answers.reviewedAt === null ? '' : ` on ${formatDate(answers.reviewedAt)}`;
    return { intent: 'info', text: `Reviewed${on}. These answers can become the effective values.` };
  }
  if (answers.state === 'rejected') {
    const on = answers.reviewedAt === null ? '' : ` on ${formatDate(answers.reviewedAt)}`;
    return {
      intent: 'warning',
      text: `Returned to the candidate${on}. They can change these answers and send the form again.`,
    };
  }
  if (answers.submittedAt !== null && answers.state === 'submitted') {
    return { intent: 'info', text: `Sent ${formatDate(answers.submittedAt)}, not reviewed.` };
  }
  return {
    intent: 'warning',
    text:
      `In progress and not sent yet. These answers are saved on ${firstName}'s profile and are not ` +
      `reviewed. HR can't change them. The form comes to Review when it is sent.`,
  };
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function timings(answers: StepAnswers): readonly KeyValueItem[] {
  const items: KeyValueItem[] = [];
  if (answers.startedAt !== null) items.push({ label: 'Started', value: formatDate(answers.startedAt) });
  if (answers.savedAt !== null) items.push({ label: 'Last saved', value: formatDate(answers.savedAt) });
  if (answers.submittedAt !== null) items.push({ label: 'Sent', value: formatDate(answers.submittedAt) });
  return items;
}

function formatMoment(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/**
 * "Signing details" (ADR-014 §3) — the one place this is shown, under the step's own capability.
 *
 * WHAT EACH LABEL PROMISES. ADR-014 is explicit that the IP and the user agent are CORROBORATING
 * and that a value is "labelled 'as reported', never 'verified'" — the platform hands us a header,
 * and `X-Forwarded-For` is something a client can write. So the label says as reported, and the
 * caption underneath says what the signature actually rests on, which is the single-person link,
 * the access code, the session and the typed name. HR reading this in a dispute should not come
 * away believing the address is proof of anything on its own.
 *
 * An absent address reads "Not recorded" rather than being dropped: a row that silently disappears
 * when no header carried an address looks identical to a form nobody signed.
 */
function signingItems(evidence: NonNullable<StepAnswers['signingDetails']>): readonly KeyValueItem[] {
  return [
    { label: 'Signed', value: formatMoment(evidence.signedAt) },
    { label: 'Name typed', value: evidence.typedName },
    { label: 'IP address, as reported', value: evidence.client.ip ?? 'Not recorded' },
    { label: 'Device, as reported', value: evidence.client.userAgent ?? 'Not recorded' },
  ];
}

export function StepAnswersPanel({ personId, instanceId, type, config, candidateFirstName }: StepAnswersPanelProps) {
  const styles = useStyles();
  const [tab, setTab] = useState<'answers' | 'candidate'>('answers');
  const query = useQuery({
    queryKey: ['step-answers', personId, instanceId],
    queryFn: () => fetchStepAnswers(personId, instanceId),
    // The app's default is `retry: 1`. A refusal here is DELIBERATE and identical to a missing
    // form (ADR-013 §4), so retrying it just asks a second time to be told no.
    retry: false,
  });

  if (query.isPending) return <Spinner size="tiny" label="Loading answers" />;

  /**
   * ONE EMPTY STATE FOR EVERY REFUSAL, and it must not say "no permission".
   *
   * The endpoint answers 404 for a form that does not exist, one belonging to somebody else, and
   * one this user may not read — a segregated health form needs `answers.restricted.read`
   * (ADR-013 §4). The client genuinely cannot tell those apart, so claiming a permission problem
   * would be a guess, and claiming the form is missing would be a lie in the restricted case.
   */
  if (query.isError) {
    return (
      <MessageBar intent="info">
        <MessageBarBody>This form is not available.</MessageBarBody>
      </MessageBar>
    );
  }

  const answers = query.data;
  const banner = bannerFor(answers, candidateFirstName);
  const done = answers.sections.filter((section) => section.state === 'done').length;

  return (
    <div className={styles.panel}>
      <TabList aria-label="Form view" selectedValue={tab} onTabSelect={(_, data) => setTab(data.value === 'candidate' ? 'candidate' : 'answers')}>
        <Tab value="answers">Answers so far</Tab>
        <Tab value="candidate">What the candidate sees</Tab>
      </TabList>

      <MessageBar intent={banner.intent}>
        <MessageBarBody>{banner.text}</MessageBarBody>
      </MessageBar>

      {tab === 'answers' ? (
        <>
          {answers.sections.length > 0 ? (
            <div className={styles.sections}>
              <Text weight="semibold">
                {done} of {answers.sections.length} sections done
              </Text>
              {answers.sections.map((section) => (
                <div key={section.key} className={styles.section}>
                  <Text>{section.label}</Text>
                  <Caption1>{SECTION_LABELS[section.state]}</Caption1>
                </div>
              ))}
            </div>
          ) : null}

          {timings(answers).length > 0 ? <KeyValueList items={timings(answers)} /> : null}

          {answers.signingDetails !== null ? (
            <div className={styles.signing}>
              <Text weight="semibold">Signing details</Text>
              <KeyValueList items={signingItems(answers.signingDetails)} />
              <Caption1>
                Corroborating only. The signature rests on the single-person link, the access code, the session and the
                name {candidateFirstName} typed.
              </Caption1>
            </div>
          ) : null}

          {answers.attempts.length > 1 ? (
            <div className={styles.attempts}>
              <Text weight="semibold">Earlier attempts</Text>
              {/* The latest attempt is what the tab already shows, so only the earlier ones are listed. */}
              {answers.attempts
                .filter((attempt) => attempt.attempt !== answers.attempt)
                .map((attempt) => (
                  <Caption1 key={attempt.attempt}>
                    Attempt {attempt.attempt + 1}, sent {formatDate(attempt.submittedAt)}
                  </Caption1>
                ))}
            </div>
          ) : null}
        </>
      ) : (
        <PortalStepRenderer
          type={type}
          config={config}
          draft={answers.value}
          onDraftChange={noEdit}
          disabled
          readOnly
          upload={noUpload}
          onAvailabilityChange={undefined}
        />
      )}
    </div>
  );
}

/**
 * Both injected capabilities are refusals, not no-ops that quietly succeed.
 *
 * `disabled` and `readOnly` already stop every registered type from offering an edit, but these
 * are the floor under that: if some future step type ignores both and calls one anyway, HR must
 * not be able to write to a candidate's form from a read-only view. A thrown error is a bug report;
 * a silent no-op is a data-loss story where HR believes they saved something.
 */
function noEdit(): never {
  throw new Error('StepAnswersPanel is read-only: a step type must not save a draft from HR’s form view.');
}

function noUpload(): Promise<never> {
  return Promise.reject(new Error('StepAnswersPanel is read-only: no upload can be staged from HR’s form view.'));
}
